/**
 * Keyboard shortcuts (docs/ux.md §12). Keys act when the board or nothing in particular has
 * focus; text fields and modal dialogs keep their own keys (U-KEY-01), and focused buttons keep
 * Space and Enter. Every action is also a button or a tap: keys are only a shortcut.
 */
import { legalActions } from '@brig/rules-engine';
import { useEffect, useRef } from 'react';
import { committed } from './fire.ts';
import { hintsFor } from './hints.ts';
import { current } from './history.ts';
import { actingUnits } from './keyboard.ts';
import { uiPhase } from './phase.ts';
import { squadronCandidates } from './turn.ts';
import { liveSquadron } from './turnMachine.ts';
import type { TurnSend, TurnSnapshot } from './useTurn.ts';

export interface HotkeyActions {
  readonly onMyFleet: () => void;
  readonly onToggleLog: () => void;
  readonly onKeys: () => void;
  /** I: the selected unit's card, as a long press or a right click shows it (U-CTX-01). */
  readonly onCard?: () => void;
  /**
   * Esc closes the topmost thing first (a picker, a sheet), then the turn's own layers: the
   * cursor, the move in port, the fire panel, and last the selection. True when it closed one.
   */
  readonly onEscape?: () => boolean;
}

const ARROWS: Readonly<Record<string, readonly [number, number]>> = {
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
};

/** The numeric keypad's eight directions, for diagonals in one key. */
const NUMPAD: Readonly<Record<string, readonly [number, number]>> = {
  Numpad7: [-1, -1],
  Numpad8: [0, -1],
  Numpad9: [1, -1],
  Numpad4: [-1, 0],
  Numpad6: [1, 0],
  Numpad1: [-1, 1],
  Numpad2: [0, 1],
  Numpad3: [1, 1],
};

function typing(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

/** The board, or no control at all: where Space, Enter, arrows and Tab belong to the game. */
function onBoard(el: Element | null): boolean {
  return el === null || el === document.body || el.closest('[data-testid="board"]') !== null;
}

export function useHotkeys(snapshot: TurnSnapshot, send: TurnSend, actions: HotkeyActions): void {
  const latest = useRef({ snapshot, actions });
  useEffect(() => {
    latest.current = { snapshot, actions };
  });

  // U-KEY-02: the board takes focus when a turn starts (and after the wind), so the keys work
  // at once; a dialog's own focus handling runs first and is left alone.
  const phase = uiPhase(snapshot);
  const lastPhase = useRef(phase);
  useEffect(() => {
    const was = lastPhase.current;
    lastPhase.current = phase;
    if (was === phase || (phase !== 'muster' && phase !== 'battle')) return;
    if (was !== 'handover' && was !== 'wind') return;
    const active = document.activeElement;
    if (active === null || active === document.body) {
      document
        .querySelector<HTMLElement>('[data-testid="board"] [role="application"]')
        ?.focus({ preventScroll: true });
    }
  }, [phase]);

  useEffect(() => {
    // Arrows held together make a diagonal step.
    const held = new Set<string>();
    const onKeyUp = (e: KeyboardEvent) => {
      held.delete(e.key);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const { snapshot: snap, actions: act } = latest.current;
      const ctx = snap.context;
      const target = document.activeElement;
      const consume = () => {
        e.preventDefault();
        e.stopPropagation();
      };
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'z') {
        if (typing(target)) return;
        consume();
        send({ type: 'UNDO' });
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const phase = uiPhase(snap);
      // U-TURN-02, U-LOG-05: R on the handover curtain, at the end or during one's own turn
      // replays the opponent's last turn.
      if (
        e.key.toLowerCase() === 'r' &&
        (phase === 'handover' || phase === 'over' || phase === 'muster' || phase === 'battle') &&
        !typing(target)
      ) {
        consume();
        send({ type: 'WATCH' });
        return;
      }
      if (typing(target) || target?.closest('[aria-modal="true"]')) return;
      const state = current(ctx.history);
      const board = onBoard(target);

      const step = ARROWS[e.key] ?? NUMPAD[e.code];
      if (step && board) {
        held.add(e.key);
        const sel = ctx.selected;
        const h = sel ? hintsFor(state, legalActions(state, state.turn.side), sel) : null;
        if (!h || h.moveCells.size + (ctx.portMove ? h.portCells.size : 0) === 0) return; // the board pans
        let [dx, dy] = step;
        if (ARROWS[e.key]) {
          dx = 0;
          dy = 0;
          for (const k of held) {
            const a = ARROWS[k];
            if (a) {
              dx += a[0];
              dy += a[1];
            }
          }
        }
        consume();
        send({ type: 'CURSOR', dx: Math.sign(dx), dy: Math.sign(dy) });
        return;
      }

      switch (e.key) {
        case ' ':
          if (!board) return;
          consume();
          if (phase === 'muster') send({ type: 'CAST' });
          else send({ type: 'SKIP_ANIM' });
          return;
        case 'Tab': {
          // U-KEY-02: within the board; after the last ship, focus moves on as usual.
          if (!board || target === document.body || target === null) return;
          const units = actingUnits(state, phase === 'muster');
          const dir = e.shiftKey ? -1 : 1;
          const i = ctx.selected ? units.indexOf(ctx.selected) : -1;
          if (units.length === 0 || (dir === 1 && i === units.length - 1) || (dir === -1 && i === 0)) return;
          consume();
          send({ type: 'NEXT_UNIT', dir });
          return;
        }
        case 'Enter':
          if (!board) return;
          consume();
          if (ctx.cursor) send({ type: 'CONFIRM_CURSOR' });
          else if (phase === 'muster' && !ctx.fire) send({ type: 'CAST' });
          else if (ctx.fire) {
            const n = committed(ctx.fire).length;
            send({ type: n >= state.ruleset.volleyMinShooters ? 'VOLLEY' : 'SHOOT' });
          } else if (phase === 'battle') send({ type: 'END_TURN' });
          else if (phase === 'handover') send({ type: 'START' });
          return;
        case 'Escape':
          if (act.onEscape?.()) {
            consume();
            return;
          }
          if (ctx.cursor) send({ type: 'CURSOR_OFF' });
          else if (ctx.portMove) send({ type: 'PORT_MOVE' });
          else if (ctx.fire) send({ type: 'CLOSE_FIRE' });
          else send({ type: 'SELECT', id: null });
          return;
        default:
          break;
      }

      switch (e.key.toLowerCase()) {
        case 'f':
          send({ type: 'NEXT_TARGET' });
          break;
        case 'v':
          send({ type: 'AIM_ALL' });
          break;
        case 's':
          // Without a ship to toggle, S keeps panning the board (WASD).
          if (phase !== 'muster' || !ctx.selected) return;
          send({ type: 'TOGGLE', id: ctx.selected });
          break;
        case 'a': {
          // U-MUST-01: every ship that may sail, or none if they all are; WASD panning otherwise.
          if (phase !== 'muster' || !board) return;
          const all = squadronCandidates(state);
          const chosen = liveSquadron(ctx);
          send({ type: all.length > 0 && chosen.length === all.length ? 'SQUADRON_CLEAR' : 'SQUADRON_ALL' });
          break;
        }
        case 'z':
          send({ type: 'UNDO' });
          break;
        case 'm':
          act.onMyFleet();
          break;
        case 'c':
          send({ type: 'TOGGLE_VIEW' });
          break;
        case 'l':
          act.onToggleLog();
          break;
        case 'i':
          if (!ctx.selected || !act.onCard) return;
          act.onCard();
          break;
        case '?':
          act.onKeys();
          break;
        default:
          return;
      }
      consume();
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
    };
  }, [send]);
}
